import { type ReactNode, useEffect, useState } from 'react';
import Loader from '@/appWrapper/Loader';
import { useSplitFeatureFlag } from '@/hooks/useSplitFeatureFlag';
import { SplitFeatureFlag } from '@/utils/split/splitConfig';
import { adoptUrlTokens } from './auth-url';
import type { AuthTokens } from './auth-cookie';

interface AuthProviderProps {
  /** Tokens taken from the URL at startup by `takeUrlTokens`, if any arrived. */
  urlTokens: AuthTokens | null;
  children: ReactNode;
}

/**
 * Decides whether tokens that arrived in the URL may be trusted, before anything
 * can issue a request. With none there is nothing to decide, so the app renders
 * immediately off the cookie and never waits on Split.
 */
export function AuthProvider({ urlTokens, children }: AuthProviderProps) {
  const [isBootstrapped, setIsBootstrapped] = useState(() => urlTokens === null);
  // No splitKey: a secondary client never reaches SDK_READY, so the flag would
  // time out and every login would silently take the legacy branch.
  const { isFeatureFlagEnabled, isReady, hasTimedout } = useSplitFeatureFlag(
    SplitFeatureFlag.CIA_67_SECURE_TOKEN_REDIRECT,
  );

  useEffect(() => {
    // Timing out resolves to OFF rather than waiting forever on an unreachable
    // Split — safe, since that branch reads the same cookie.
    if (isBootstrapped || !(isReady || hasTimedout)) return;

    adoptUrlTokens(urlTokens, { secureTokenRedirect: isFeatureFlagEnabled });
    setIsBootstrapped(true);
  }, [isBootstrapped, isReady, hasTimedout, isFeatureFlagEnabled, urlTokens]);

  return isBootstrapped ? <>{children}</> : <Loader />;
}
